import * as couponService from '@cart-services/core/couponService'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { type MergedCouponCriterionWithCoupon, type SelectedCouponGiveawayProductMap } from '@cart-services/types'
import { type CartFormValues } from '@cart-services/zod-schema/cartFormSchema'
import { DialogSelectGiveaway, DIALOG_SELECT_GIVEAWAY } from '@cart-ui/GiveawaySelect/DialogSelectGiveaway'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Fade } from '@pets-ui/Fade/Fade'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useStmCart } from '../../CartProvider'
import { CartCard } from '../CartCard'
import { CartCardBody } from '../CartCardBody'
import { CartCardHeader } from '../CartCardHeader'
import { GiveawaySelectController } from './GiveawaySelectController'

interface DialogSelectGiveawayExtraProps {
  couponCriterionId?: number
}

interface GiveawayListProps {
  appliedGiveaways: MergedCouponCriterionWithCoupon[] | undefined
  onOpenDialog: (criterionId: number) => void
}

const GiveawayList = ({ appliedGiveaways, onOpenDialog }: GiveawayListProps) => {
  const { formState: { errors } } = useFormContext<CartFormValues>()
  return appliedGiveaways?.map((giveaway, index) => {
    const { coupon, ...criterion } = giveaway
    return (
      <VStack key={criterion.id} gap={1}>
        <GiveawaySelectController
          index={index}
          coupon={coupon}
          criterion={criterion}
          onOpenDialog={onOpenDialog}
        />
        {errors?.giveaways?.[index]?.message && <Text color={colors.red600}>{errors.giveaways[index]?.message}</Text>}
      </VStack>
    )
  })
}

export const CartGiveaways = () => {
  const { isCartInfoValidating, projectCode, countryCode, campaignId, campaignCode, platform } = useStmCart()
  const {
    setDefaultSelectedGiveaways,
    appliedGiveaways,
    updateSelectedGiveaways,
    selectedCouponGiveawayMap,
  } = useFpB2bCouponService(campaignId, projectCode, countryCode, campaignCode, platform)
  const { clearErrors } = useFormContext()
  const [selectingCriterionId, setSelectingCriterionId] = useState<number>()
  const { open, closeDialog, openDialog } = useDialog<DialogSelectGiveawayExtraProps>(DIALOG_SELECT_GIVEAWAY)

  useEffect(() => {
    /**
     * 驗證購物車資料時先不更新贈品
     * 否則重新取完商品資料後就會先觸發更新了所選的贈品，也就不會觸發庫存驗證的錯誤提示
     */
    if (isCartInfoValidating) return
    setDefaultSelectedGiveaways()
  }, [setDefaultSelectedGiveaways, isCartInfoValidating])

  const handleOpenDialog = useCallbackEvent((criterionId: number) => {
    setSelectingCriterionId(criterionId)
    openDialog({ couponCriterionId: criterionId })
  })

  const currentGiveaway = useMemo(() => {
    return appliedGiveaways?.find(giveaway => giveaway.id === selectingCriterionId)
  }, [appliedGiveaways, selectingCriterionId])

  const currentGiveawayIndex = useMemo(() => {
    return appliedGiveaways?.findIndex(giveaway => giveaway.id === selectingCriterionId) ?? -1
  }, [appliedGiveaways, selectingCriterionId])

  const currentGiveawayProps = useMemo(() => {
    if (!currentGiveaway) return null
    const { coupon, ...criterion } = currentGiveaway
    return couponService.getGiveawaySelectProps(coupon, criterion, selectedCouponGiveawayMap)
  }, [currentGiveaway, selectedCouponGiveawayMap])

  const handleSubmit = useCallbackEvent((selectedCouponGiveawayProductMap: SelectedCouponGiveawayProductMap) => {
    if (!currentGiveawayProps || currentGiveawayIndex === -1) return

    clearErrors(`giveaways.${currentGiveawayIndex}`)
    updateSelectedGiveaways(currentGiveawayProps.couponCriterionId, selectedCouponGiveawayProductMap)
    closeDialog()
  })

  return (
    <>
      <Fade in={!!appliedGiveaways?.length}>
        <CartCard>
          <CartCardHeader title="贈品" />
          <CartCardBody>
            <VStack gap={2.5}>
              <GiveawayList appliedGiveaways={appliedGiveaways} onOpenDialog={handleOpenDialog} />
            </VStack>
          </CartCardBody>
        </CartCard>
      </Fade>
      {currentGiveawayProps && (
        <DialogSelectGiveaway
          key={selectingCriterionId}
          open={open}
          onClose={closeDialog}
          title={currentGiveawayProps.title}
          options={currentGiveawayProps.options}
          isRepeatable={currentGiveawayProps.isRepeatable}
          isGiveAll={currentGiveawayProps.isGiveAll}
          isStockPartiallyNotEnough={currentGiveawayProps.isStockPartiallyNotEnough}
          accumulatedGiftSelectionCount={currentGiveawayProps.accumulatedGiftSelectionCount}
          defaultGiveawayValues={currentGiveawayProps.defaultGiveawayValues}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}
